import React, { useState } from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';

const CloseAccount = ({ user, onLogout }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/app/api/account/close.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // Log the user out after closing
        onLogout();
      } else {
        setError(data.message || 'Failed to close account.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Close Account</h1>
          <p className="text-gray-600">Permanently deactivate your MageGuard account.</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Before you close your account</h2>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Your account will be marked as closed immediately.</li>
                <li>You will not be able to log in while your account is closed.</li>
                <li>All sites assigned to you will be reassigned to the admin.</li>
                <li>Your account will be permanently deleted in the next batch run.</li>
              </ul>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!confirmed ? (
            <div>
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to close your account? This action cannot be easily undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmed(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Yes, Close My Account
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-red-700 mb-4">
                This is your final confirmation. Click below to close your account now.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition text-sm font-medium"
                >
                  {loading ? 'Closing...' : 'Confirm — Close My Account'}
                </button>
                <button
                  onClick={() => setConfirmed(false)}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:cursor-not-allowed transition text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloseAccount;
